import * as React from "react";

import { cn } from "@/shared/utils/utils";
import { useBranding } from "@/shared/contexts/BrandingContext";

/**
 * Brand-aware Input Component
 *
 * Automatically adapts focus states based on active brand.
 * Meets WCAG 2.1 AA accessibility standards.
 */

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    const { isAegis360 } = useBranding();

    // Brand-aware colors
    const borderColor = isAegis360 ? "border-blue-200" : "border-emerald-200";
    const borderHoverColor = isAegis360
      ? "hover:border-blue-300"
      : "hover:border-emerald-300";
    const borderFocusColor = isAegis360
      ? "focus:border-blue-500 focus-visible:border-blue-500"
      : "focus:border-emerald-500 focus-visible:border-emerald-500";
    const focusRingColor = isAegis360
      ? "focus-visible:ring-blue-500/20"
      : "focus-visible:ring-emerald-500/20";
    const focusBgColor = isAegis360
      ? "focus:bg-blue-50/20 hover:bg-blue-50/30"
      : "focus:bg-emerald-50/20 hover:bg-emerald-50/30";

    return (
      <input
        type={type}
        className={cn(
          "flex h-11 w-full rounded-lg border-2 bg-background px-4 py-2 text-base ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "md:text-sm transition-all duration-200 ease-in-out",
          "focus:shadow-token-sm",
          // Brand-aware colors
          borderColor,
          borderHoverColor,
          borderFocusColor,
          focusRingColor,
          focusBgColor,
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
